import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useHousehold } from "@/app/useHousehold";
import { useAuth } from "@/auth/AuthProvider";
import {
  downloadHouseholdData,
  useEraseHousehold,
  useErasePerson,
  type ErasureReport,
} from "@/api/privacy";
import {
  ErrorNote,
  Field,
  Panel,
  PageTitle,
  Section,
  buttonClass,
  inputClass,
  secondaryButtonClass,
} from "@/components/ui";
import { useT } from "@/i18n/LocaleProvider";

/**
 * Your data: take it with you, or have it deleted (K-38, `project.md` §7).
 *
 * Three things, in the order somebody wants them. Download first, because
 * anybody about to delete an account should be offered the copy before the
 * button that destroys it. Then the household. Then the account.
 *
 * **Both deletions ask for a word rather than a yes.** This is the only place in
 * Kontora where a delete is not a soft delete, and a mis-tap here is not
 * recoverable by any means — so the confirmation is something a person has to
 * read and type, which is the cheapest available way of making sure they meant
 * it.
 */
export function DataPage() {
  const { householdId = "" } = useParams();
  const t = useT();
  const navigate = useNavigate();
  const household = useHousehold();
  const { logout } = useAuth();

  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState<unknown>();

  return (
    <div className="max-w-2xl">
      <PageTitle lead={t("data.lead")}>{t("data.title")}</PageTitle>

      <Panel>
        <h2 className="text-sm font-semibold text-ink">{t("data.download")}</h2>
        <p className="mt-1 text-sm text-muted">{t("data.downloadHint")}</p>
        <button
          type="button"
          disabled={downloading}
          onClick={() => {
            setDownloadError(undefined);
            setDownloading(true);
            downloadHouseholdData(householdId)
              .catch(setDownloadError)
              .finally(() => setDownloading(false));
          }}
          className={`${buttonClass} mt-3 min-h-11`}
          data-testid="download-data"
        >
          {downloading ? t("data.preparing") : t("data.downloadButton")}
        </button>
        <ErrorNote error={downloadError} />
      </Panel>

      <Section title={t("data.dangerZoneTitle")} className="mt-10">
        <p className="text-sm text-muted">{t("data.dangerZoneHint")}</p>
        <div className="mt-4 space-y-6">
          <EraseHousehold
            householdId={householdId}
            name={household?.name ?? ""}
            onErased={() => navigate("/")}
          />

          <ErasePerson onErased={() => void logout()} />
        </div>
      </Section>
    </div>
  );
}

/** Deleting the household: irreversible, and confirmed by its own name. */
function EraseHousehold({
  householdId,
  name,
  onErased,
}: {
  householdId: string;
  name: string;
  onErased: () => void;
}) {
  const t = useT();
  const [confirm, setConfirm] = useState("");
  const erase = useEraseHousehold(householdId);

  return (
    <Panel as="section">
      <h3 className="text-sm font-semibold text-ink">{t("data.eraseHousehold")}</h3>
      <p className="mt-1 text-sm text-muted">{t("data.eraseHouseholdHint")}</p>

      <Field
        label={t("data.typeTheName", { name })}
        htmlFor="erase-household-confirm"
        error={undefined}
      >
        <input
          id="erase-household-confirm"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          className={`${inputClass} min-h-11 text-base sm:text-sm`}
          data-testid="erase-household-confirm"
        />
      </Field>

      <button
        type="button"
        // Enabled only once the name matches. The server checks it too — this is
        // the half that stops somebody reaching the button by accident.
        disabled={confirm !== name || erase.isPending || name === ""}
        onClick={() => erase.mutate(confirm, { onSuccess: onErased })}
        className={`${secondaryButtonClass} mt-4 min-h-11 border-overspend text-overspend`}
        data-testid="erase-household"
      >
        {erase.isPending ? t("data.erasing") : t("data.eraseHouseholdButton")}
      </button>
      <ErrorNote error={erase.error} />
    </Panel>
  );
}

/** Erasing the person: what Kontora can do, and what it cannot. */
function ErasePerson({ onErased }: { onErased: () => void }) {
  const t = useT();
  const { user } = useAuth();
  const email = user?.profile.email ?? "";

  const [confirm, setConfirm] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const [report, setReport] = useState<ErasureReport>();
  const erase = useErasePerson();

  if (report) {
    return (
      <Panel as="section">
        <h3 className="text-sm font-semibold text-ink">{t("data.erasedTitle")}</h3>
        {/* The note comes from the server rather than from here: the thing
            Kontora cannot do is a fact about the deployment, not a sentence a
            screen should be inventing. */}
        <p className="mt-1 text-sm text-muted">{report.note}</p>
        <button
          type="button"
          onClick={onErased}
          className={`${buttonClass} mt-4 min-h-11`}
          data-testid="erased-sign-out"
        >
          {t("app.signOut")}
        </button>
      </Panel>
    );
  }

  return (
    <Panel as="section">
      <h3 className="text-sm font-semibold text-ink">{t("data.erasePerson")}</h3>
      <p className="mt-1 text-sm text-muted">{t("data.erasePersonHint")}</p>

      <Field label={t("data.typeYourEmail")} htmlFor="erase-person-confirm">
        <input
          id="erase-person-confirm"
          value={confirm}
          onChange={(e) => setConfirm(e.target.value)}
          className={`${inputClass} min-h-11 text-base sm:text-sm`}
          data-testid="erase-person-confirm"
        />
      </Field>

      <label className="mt-3 flex items-start gap-2 text-sm text-muted">
        <input
          type="checkbox"
          checked={acknowledged}
          onChange={(e) => setAcknowledged(e.target.checked)}
          className="mt-1"
          data-testid="erase-households"
        />
        {t("data.eraseHouseholdsToo")}
      </label>

      <button
        type="button"
        disabled={confirm !== email || email === "" || erase.isPending}
        onClick={() =>
          erase.mutate(
            { confirm, erase_households: acknowledged },
            { onSuccess: setReport },
          )
        }
        className={`${secondaryButtonClass} mt-4 min-h-11 border-overspend text-overspend`}
        data-testid="erase-person"
      >
        {erase.isPending ? t("data.erasing") : t("data.erasePersonButton")}
      </button>
      <ErrorNote error={erase.error} />
    </Panel>
  );
}
